import queryString from 'query-string'
import isEmpty from 'lodash/isEmpty'

import { buildUserAttributes } from '@/utils/launchDarkly'
import { fetchOrgHierarchy } from '@/reducers/orgHierarchy/orghierarchy.actions'
import { apiService, fetchingAPI } from '@/api'
import { clearStoredTheme } from '@/utils/hooks/useTheme'
import { clearAuthMessage, updateAuthMessage, openModal } from '../ui/ui.redux'
import { loginUser, logoutUser } from './currentUser.redux'
import { setPendingToken } from './mfa.redux'
import { clearSSOReturnPath, peekSSOReturnPath, safeNextPath, withNext } from './loginNext.helpers'

export const authenticateUser = (userData, setSubmitting) => {
  return async (dispatch) => {
    const { email, password, token, history, location, ldClient } = userData
    let flags = { showCommandCenter: true }
    let commandCenterEnabled = true

    dispatch(clearAuthMessage())

    try {
      const { next: requestedNext } = queryString.parse(location.search)
      // An SSO login comes back as /login?token=... with no next; its return path waits in
      // sessionStorage until a login succeeds.
      const next = isEmpty(requestedNext) && !isEmpty(token) ? peekSSOReturnPath() : requestedNext
      const response = await fetch(`${apiService.auth}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, token }),
      })

      if (response.status === 401) {
        const authErr = await response.text()
        dispatch(updateAuthMessage('error', authErr || 'Incorrect username or password.'))
        if (setSubmitting) return setSubmitting(false)
        return null
      }

      if (response.status >= 500) {
        dispatch(
          updateAuthMessage(
            'error',
            'The server encountered an internal error and was unable to login.'
          )
        )
        if (setSubmitting) return setSubmitting(false)
        return null
      }

      const currentUser = await response.json()

      if (currentUser.error) {
        dispatch(updateAuthMessage('error', 'Something went wrong.'))
        if (setSubmitting) return setSubmitting(false)
        return null
      }

      // if a pending_token is received, that means the organization enforces 2 factor authentication
      // and the user must complete the code challenge to get the actual token
      if (currentUser.pending_token) {
        // currentUser will have keys pending_token, mfa_type, organization_id, username
        dispatch(setPendingToken(currentUser))
        // Carry next through MFA so the user still lands where they were headed.
        return history.push(withNext('/login_mfa', next))
      }

      // The login succeeded, so a saved SSO return path has done its job.
      clearSSOReturnPath()

      // for hierarchy managers, the user's organizationid will change to whichever org they selected
      // we store own_organization_id as the user's true organization id
      currentUser.own_organization_id = currentUser.organizationid

      // Persist current user in localstorage so user remains logged in when they refresh the page
      localStorage.setItem('User', JSON.stringify(currentUser))

      // Log user into Redux
      dispatch(loginUser(currentUser))
      dispatch(clearAuthMessage())

      // Initialize LaunchDarkly
      if (ldClient) {
        await ldClient.identify(buildUserAttributes(currentUser))
        flags = ldClient.allFlags()
        commandCenterEnabled = flags?.showCommandCenter !== false
      }

      const defaultDestination =
        currentUser.organizationid === 1
          ? '/organizations'
          : commandCenterEnabled
            ? '/live-listen'
            : '/explorer'

      // This line prevents some back end errors that are blowing up the logs
      if (next === '/users/' && currentUser.organizationid === 1) {
        return history.push('/live-listen')
      }
      const redirect = safeNextPath(next) || defaultDestination

      return history.push(redirect)
    } catch (err) {
      console.error(err)

      dispatch(updateAuthMessage('error', 'Something went wrong, please try again.'))
      if (setSubmitting) return setSubmitting(false)
      return null
    }
  }
}

// Forgets the session saved in this browser. Doesn't touch Redux or navigate.
export const clearSavedSession = () => {
  localStorage.removeItem('User')
  localStorage.removeItem('balto_org_id')
  clearStoredTheme()
}

export const localLogoutUser = ({ history, location, ldClient }) => {
  return async (dispatch) => {
    const redirect = `/login${location && location.pathname !== '/login' ? `?next=${location.pathname}` : ''}`

    dispatch(logoutUser())

    clearSavedSession()

    if (ldClient) {
      ldClient.identify({ anonymous: true })
    }

    history.push(redirect)
  }
}

export const unauthenticateUser = ({ history, location, ldClient, callback }) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(`${apiService.auth}/api/logout`, 'POST', dispatch)
    } catch (err) {
      console.error(err)
    }

    dispatch(localLogoutUser({ history, location, ldClient, callback }))
  }
}

export const resetUserPassword = (email, setSubmitting) => async (dispatch) => {
  dispatch(clearAuthMessage())

  try {
    const response = await fetch(`${apiService.auth}/api/reset_password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username: email }),
    })

    if (response.status !== 200) {
      dispatch(updateAuthMessage('error', 'Something went wrong, please try again.'))
      return setSubmitting(false)
    }

    const currentUser = await response.json()

    if (currentUser.error) {
      dispatch(updateAuthMessage('error', currentUser.error))
      return setSubmitting(false)
    }

    dispatch(
      updateAuthMessage(
        'success',
        "We sent an email with a link to reset your password. If you don't see the email from noreply@baltocloud.com, please check your spam folder."
      )
    )
    return setSubmitting(false)
  } catch (err) {
    dispatch(updateAuthMessage('error', 'Something went wrong, please try again.'))
    return setSubmitting(false)
  }
}

export const fetchCurrentUserPermissions = (userId, organizationId) => {
  return async (dispatch, getState) => {
    const { currentUser } = getState()
    try {
      const userPermissions = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/users/${userId}/permissions`,
        'GET',
        dispatch,
        undefined,
        undefined,
        undefined,
        true
      )

      if (!isEmpty(userPermissions)) {
        dispatch(loginUser({ ...currentUser, ...userPermissions }))
        localStorage.setItem('User', JSON.stringify({ ...currentUser, ...userPermissions }))

        if (!userPermissions.metrics_job_function) {
          dispatch(openModal('metricsJobFunctionModal'))
        }
      }

      // get org hierarchy (other orgs user has permission for) if user is hierarchy manager
      if (userPermissions?.hierarchy_manager) {
        dispatch(fetchOrgHierarchy())
      }
    } catch (err) {
      console.error(err)
    }
  }
}
